import { useState, useCallback } from 'react'
import { POIResult } from '@/shared/api/routes'
import { usePOISearch } from './usePOISearch'

export interface UsePOIHandlersProps {
  routeId: string | null
  tripId: string | null
  getWaypointsByTripAndRoute: (tripId: string, routeId: string) => Promise<any[]>
  fetchWaypoints: (params?: any) => Promise<any[]>
  onPOISaved?: () => Promise<void>
}

export interface UsePOIHandlersReturn {
  // POI state
  selectedPOI: POIResult | null
  setSelectedPOI: (poi: POIResult | null) => void
  
  // POI search state (from usePOISearch)
  poiResults: POIResult[]
  isPOISearching: boolean
  poiSearchError: string | null
  
  // POI handlers
  handlePOISelect: (poi: POIResult) => void
  handleSavePOIToTrip: (poi: POIResult, tripId: string, notes?: string) => Promise<void>
  handlePOISearch: (params: any) => Promise<void>
  handleClearPOIResults: () => void
}

export function usePOIHandlers({
  routeId,
  tripId,
  getWaypointsByTripAndRoute,
  fetchWaypoints,
  onPOISaved
}: UsePOIHandlersProps): UsePOIHandlersReturn {
  // POI selection state
  const [selectedPOI, setSelectedPOI] = useState<POIResult | null>(null)
  
  // POI search hook
  const {
    poiResults,
    isSearching: isPOISearching,
    searchError: poiSearchError,
    searchPOI,
    searchPOIAlongRoute,
    savePOIAsWaypoint,
    clearResults: clearPOIResults
  } = usePOISearch()
  
  // Handle POI selection
  const handlePOISelect = useCallback((poi: POIResult) => {
    setSelectedPOI(poi)
  }, [])
  
  // Handle saving POI to trip
  const handleSavePOIToTrip = useCallback(async (
    poi: POIResult,
    targetTripId: string,
    notes?: string
  ) => {
    if (!routeId) {
      return
    }

    try {
      await savePOIAsWaypoint(poi, targetTripId, routeId, notes)
      setSelectedPOI(null)
      
      // Refresh waypoints if we're viewing the same trip
      // Use fetchWaypoints to update the main waypoints state
      // Don't filter by routeId - we want all waypoints visible on map
      if (targetTripId) {
        await fetchWaypoints({ trip_id: targetTripId })
      } else {
        await fetchWaypoints({})
      }
      
      // Call optional callback
      if (onPOISaved) {
        await onPOISaved()
      }
    } catch (error) {
      throw error
    }
  }, [routeId, savePOIAsWaypoint, fetchWaypoints, onPOISaved])
  
  // Handle POI search (supports both along-route and nearby modes)
  const handlePOISearch = useCallback(async (params: { routeId?: string; categories: string[]; maxDistance: number; legalOnly?: boolean; centerLat?: number; centerLng?: number; tripId?: string; tripDay?: number }) => {
    // Nearby search: use centerLat/centerLng
    if (params.centerLat != null && params.centerLng != null) {
      await searchPOI({
        latitude: params.centerLat,
        longitude: params.centerLng,
        categories: params.categories,
        radius: params.maxDistance,
        legalOnly: params.legalOnly,
      })
    } 
    // Along-route search: use routeId with optional tripId and tripDay
    else if (params.routeId) {
      await searchPOIAlongRoute({
        routeId: params.routeId,
        categories: params.categories,
        maxDistance: params.maxDistance,
        legalOnly: params.legalOnly,
        tripId: params.tripId,
        tripDay: params.tripDay,
      })
    } else {
      throw new Error('Either routeId or centerLat/centerLng must be provided for POI search')
    }
  }, [searchPOI, searchPOIAlongRoute])
  
  // Handle clearing POI results
  const handleClearPOIResults = useCallback(() => {
    clearPOIResults()
  }, [clearPOIResults])
  
  return {
    // POI state
    selectedPOI,
    setSelectedPOI,
    
    // POI search state
    poiResults,
    isPOISearching,
    poiSearchError,
    
    // POI handlers
    handlePOISelect,
    handleSavePOIToTrip,
    handlePOISearch,
    handleClearPOIResults
  }
}

